<template>
  <div class="events-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>
      <h1>Foglalkozás napló</h1>
      <p>{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok foglalkozásai' : 'Összes foglalkozás áttekintése és kezelése' }}</p>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Betöltés...</p>
    </div>

    <div v-if="!loading">
      <!-- Search and Filter Section -->
      <div class="filters-section">
        <div class="search-group">
          <div class="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input 
              v-model="searchQuery" 
              type="text" 
              class="modern-input" 
              placeholder="Események keresése csoportnév, státusz vagy dátum alapján"
            />
          </div>
          <button @click="toggleSeasonFilter" class="modern-btn modern-btn--secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
            </svg>
            {{ showAllSeasons ? "Csak az aktuális szezon" : "Összes szezon" }}
          </button>
        </div>
      </div>

      <!-- Events Table -->
      <div class="table-container">
        <div class="table-wrapper">
          <table class="modern-table">
            <thead>
              <tr>
                <th>
                  <div class="th-content">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Csoport neve
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    Ütemezett dátum
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                    </svg>
                    Státusz
                  </div>
                </th>
                <th>
                  <div class="th-content">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    Szezon
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in paginatedEvents" :key="event.event_id" @click="goToEvent(event.event_id)" class="table-row event-row">
                <td class="group-cell">
                  <div class="cell-content">
                    <div class="group-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    </div>
                    <span class="group-name">{{ getGroupNameById(event.grp_id) }}</span>
                  </div>
                </td>
                <td class="date-cell">
                  <div class="date-content">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    {{ formatDate(event.set_date) }}
                  </div>
                </td>
                <td class="status-cell">
                  <span class="status-badge" :class="getStatusClass(event.status.String)">
                    {{ responseEnum[event.status.String] }}
                  </span>
                </td>
                <td class="season-cell">
                  <span class="season-badge">
                    {{ event.season.Valid ? event.season.String : 'Aktuális' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="pagination-container">
        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="modern-btn modern-btn--secondary" :class="{ 'disabled': currentPage === 1 }">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15,18 9,12 15,6"/>
          </svg>
          Előző
        </button>
        <span>{{ currentPage }} / {{ totalPages }} oldal</span>
        <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="modern-btn modern-btn--secondary" :class="{ 'disabled': currentPage === totalPages }">
          Következő
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9,18 15,12 9,6"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      groupsData: [], // Data for the groups
      eventsData: [], // Data for the events
      searchQuery: "", // Search query for filtering
      loading: true, // Loading state for API call
      currentPage: 1, // Current page for pagination
      itemsPerPage: 10, // Number of items per page
      showAllSeasons: false, // Toggle to show all seasons or only current season
      isAdmin: false,
      isTeacher: false,
      userId: null,
      responseEnum: {
        "20": "Törölve", // Example status mappings
        "10": "Megtartva",
        "6": "Adminoknak email kiküldve!",
        "5": "Emlékeztető email kiküldve a szülőknek!",
        "0": "",
        "" : "" 
      },
    };
  },
  mounted() {
    this.getIsAdmin();
    this.getIsTeacher();
    this.getUserId();
    this.getCalendarData();
  },
  computed: {
    filteredEvents() {
      return this.eventsData
        .filter(event => {
          // Apply season filter only when `showAllSeasons` is false
          if (!this.showAllSeasons && event.season.Valid) {
            return false; // Hide past seasons
          }

          // Search filtering
          const groupName = this.getGroupNameById(event.grp_id).toLowerCase();
          const status = this.responseEnum[event.status.String].toLowerCase();
          const search = this.searchQuery.toLowerCase();

          // Date matching
          const eventDate = this.formatDate(event.set_date).toLowerCase();
          const isDateMatch = eventDate.includes(search) || 
                              this.formatDateMonth(event.set_date).toLowerCase().includes(search);

          return groupName.includes(search) || status.includes(search) || isDateMatch;
        })
        .sort((a, b) => new Date(b.set_date) - new Date(a.set_date)); // Sort events by date (newest first)
    },

    paginatedEvents() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredEvents.slice(startIndex, startIndex + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredEvents.length / this.itemsPerPage);
    },
  },
  methods: {
    getIsAdmin() {
      this.isAdmin = this.$isAdmin();
    },
    getIsTeacher() {
      this.isTeacher = this.$isTeacher();
    },
    getUserId() {
      this.userId = this.$getUserId();
    },
    goToEvent(eventId) {
      this.$router.push(`/event/${eventId}`);
    },
    async getCalendarData() {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/getcalendar', {
          like_date: ""
        });

        if (response.data.message === "done") {
          let allGroupsData = response.data.value.groups_data;
          let allEventsData = response.data.value.events_data;
          
          // If user is a teacher, filter to only show their assigned groups
          if (this.isTeacher && !this.isAdmin) {
            const teacherId = this.userId;
            if (!teacherId) {
              throw new Error('Teacher ID not found');
            }
            
            // Get teacher's assigned groups
            const teacherResponse = await fetch(`${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${teacherId}`);
            if (teacherResponse.ok) {
              const teacherData = await teacherResponse.json();
              const assignedGroupIds = teacherData.groups.map(g => g.id);
              
              // Filter groups and events to only show assigned ones
              this.groupsData = allGroupsData.filter(group => assignedGroupIds.includes(group.id));
              this.eventsData = allEventsData.filter(event => assignedGroupIds.includes(event.grp_id));
            } else {
              this.groupsData = [];
              this.eventsData = [];
            }
          } else {
            // For admin users, show all data
            this.groupsData = allGroupsData;
            this.eventsData = allEventsData;
          }
          
          this.loading = false; // Data loaded
        }
      } catch (error) {
        console.error('Hiba történt a naptár adatok lekérésekor:', error);
        this.loading = false;
      }
    },
    getGroupNameById(groupId) {
      const group = this.groupsData.find(g => g.id === groupId);
      return group ? group.name : 'Ismeretlen csoport';
    },
    formatDate(dateString) {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', options);
    },
    formatDateMonth(dateString) {
      const options = { month: 'long' };
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', options);
    },
    changePage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.currentPage = pageNumber;
      }
    },
    toggleSeasonFilter() {
      this.showAllSeasons = !this.showAllSeasons;
    },
    getStatusClass(status) {
      switch(status) {
        case "20": return "status-badge--deleted";
        case "10": return "status-badge--completed";
        case "6": return "status-badge--admin-email";
        case "5": return "status-badge--reminder-email";
        case "0": return "status-badge--pending";
        default: return "status-badge--default";
      }
    }
  },
};
</script>

<style scoped>
.events-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: var(--color-background);
  min-height: 100vh;
}

.content-header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--button-primary);
  border-radius: 16px;
  margin-bottom: 1rem;
  color: white;
}

.content-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-heading);
  margin: 0 0 0.5rem 0;
}

.content-header p {
  color: var(--color-text-muted);
  font-size: 1.1rem;
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--button-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.filters-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.search-group {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 300px;
}

.search-input-wrapper svg {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}

.modern-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background: var(--card-bg);
  color: var(--color-text);
  font-size: 1rem;
  transition: all 0.2s ease;
}

.modern-input:focus {
  outline: none;
  border-color: var(--button-primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.modern-input::placeholder {
  color: var(--color-text-muted);
}

.modern-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
}

.modern-btn--secondary {
  background: var(--color-background-soft);
  color: var(--color-text);
  border: 2px solid var(--color-border);
}

.modern-btn--secondary:hover:not(.disabled) {
  background: var(--color-background-mute);
  border-color: var(--button-primary);
  color: var(--button-primary);
  transform: translateY(-1px);
}

.modern-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-container {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
  margin-bottom: 2rem;
}

.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: var(--color-background-soft);
  padding: 1rem 1.5rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.th-content svg {
  color: var(--button-primary);
}

.table-row {
  transition: all 0.2s ease;
  cursor: pointer;
}

.table-row:hover {
  background: var(--color-background-soft);
}

.table-row td {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
}

.cell-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.group-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--button-primary);
  border-radius: 8px;
  color: white;
  flex-shrink: 0;
}

.group-name {
  color: var(--color-text);
  font-weight: 500;
}

.date-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text);
}

.date-content svg {
  color: var(--button-primary);
}

.status-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
}

.status-badge--completed {
  background: var(--success);
  color: white;
}

.status-badge--deleted {
  background: var(--error);
  color: white;
}

.status-badge--admin-email {
  background: var(--warning);
  color: white;
}

.status-badge--reminder-email {
  background: var(--info);
  color: white;
}

.status-badge--pending {
  background: var(--color-background-soft);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
}

.status-badge--default {
  background: var(--color-background-soft);
  color: var(--color-text);
}

.season-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  background: var(--color-background-soft);
  color: var(--color-text);
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid var(--color-border);
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
}

.pagination-container span {
  color: var(--color-text-muted);
  font-weight: 500;
}

@media (max-width: 768px) {
  .events-container {
    padding: 1rem;
  }

  .content-header {
    padding: 1.5rem;
  }

  .content-header h1 {
    font-size: 2rem;
  }

  .header-icon {
    width: 56px;
    height: 56px;
  }

  .search-group {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-wrapper {
    min-width: auto;
  }

  .modern-table th,
  .table-row td {
    padding: 0.75rem 1rem;
  }

  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
